import { isAuthenticated, isOrganizationUser } from "@biggeo/bg-common";
import React, { Suspense } from "react";
import { Modal } from "../../modal/Modal";
import { SignInUpLayout } from "../../user/components/SignInUpLayout";
import SignInContainer from "../../user/containers/SignInContainer";
import { useAuthenticatedUser } from "../../user/redux/hooks";
import { UserContextProvider } from "./UserContextProvider";

export type RequiresAuthProps = React.PropsWithChildren<{
    readonly hideHeader?: boolean;
    readonly hideSidebar?: boolean;
    readonly currentPath?: string;
    readonly adminOnly?: boolean;
    readonly ownerOnly?: boolean;
}>;

/**
 * RequiresAuth component
 *
 * This component is used to handle user level authentication checks based on authentication state
 * and user role. It performs the following:
 *
 * - Verifies if user is authenticated through valid JWT token
 * - If adminOnly=true, checks if the authenticated user has admin privileges
 * - If isOwner=true, checks if the authenticated user is an owner of the organization
 * - Renders the children component with StudioMainLayout if all checks pass
 * - Redirects unauthenticated users to the sign-in page
 * - Redirects non-admin users to the sign-in page when adminOnly=true
 *
 * @param hideHeader - hide header component
 * @param hideSidebar - hide sidebar component
 * @param currentPath - current path of the page
 * @adminOnly - if true, only admin users can access the page
 * @ownerOnly - if true, only owner users can access the page
 * @param children - children component
 *
 */
const RequiresAuth = ({
    children,
    adminOnly,
    ownerOnly,
}: RequiresAuthProps) => {
    const authUser = useAuthenticatedUser();

    if (
        isAuthenticated(authUser) &&
        isOrganizationUser(authUser) &&
        (adminOnly ? authUser.user.isAdmin : true) &&
        (ownerOnly ? authUser.organizationUser?.isOwner : true)
    ) {
        return (
            <UserContextProvider user={authUser}>
                <Modal />
                {children}
            </UserContextProvider>
        );
    }

    return (
        <SignInUpLayout>
            <Suspense fallback={<div />}>
                <Modal />
                <SignInContainer />
            </Suspense>
        </SignInUpLayout>
    );
};

export default RequiresAuth;
